import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTier } from '../context/TierContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ListViewHeader } from '../components/ui/list-view-header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '../components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  FileText, 
  Search, 
  Eye, 
  Trash2, 
  Calendar,
  Building2,
  MapPin,
  Truck,
  MoreHorizontal,
  Pencil,
  Camera,
  Upload,
  Loader2,
  Sparkles,
  CheckCircle2,
  AlertCircle,
  Brain,
  Image as ImageIcon,
  Check,
  X,
  Send,
  FileDown,
  ExternalLink,
  Filter,
  User,
  Clock,
  Wrench,
  ClipboardCheck,
  RotateCcw,
  RefreshCw,
  Plus,
  Package,
  Mountain,
  Mail,
  MailCheck,
  MailX,
  MailOpen,
  History
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { ConfirmationDialog } from '../components/ui/confirmation-dialog';
import { format, addDays, subDays, startOfWeek, endOfWeek } from 'date-fns';

const Dockets = () => {
  const { api, user } = useAuth();
  const { isAiAvailable } = useTier();
  const [dockets, setDockets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('today'); // Default to TODAY
  const [selectedDocket, setSelectedDocket] = useState(null);
  const [tenantSettings, setTenantSettings] = useState({
    currency_symbol: '$',
    tax_name: 'GST',
    tax_rate: 10,
    date_format: 'DD/MM/YYYY',
    openai_api_key: null,
    xero_connected: false,
    myob_connected: false,
    connected_accounting_package: null
  });

  // Edit Dialog State
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingDocket, setEditingDocket] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [usedPriceList, setUsedPriceList] = useState(null);

  // XERO Dialog State
  const [xeroDialogOpen, setXeroDialogOpen] = useState(false);
  const [xeroPushing, setXeroPushing] = useState(false);

  // MYOB Dialog State
  const [myobDialogOpen, setMyobDialogOpen] = useState(false);
  const [myobPushing, setMyobPushing] = useState(false);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [docketToDelete, setDocketToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Email Docket State
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailDocket, setEmailDocket] = useState(null);
  const [emailAddress, setEmailAddress] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [emailStatusLoading, setEmailStatusLoading] = useState(false);
  const [emailStatusDialogOpen, setEmailStatusDialogOpen] = useState(false);

  // OCR State
  const [ocrDialogOpen, setOcrDialogOpen] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrImage, setOcrImage] = useState(null);
  const [ocrImagePreview, setOcrImagePreview] = useState(null);
  const [ocrResult, setOcrResult] = useState(null);
  const fileInputRef = useRef(null);

  const fetchTenantSettings = async () => {
    try {
      const response = await api.get(`/tenants/${user.client_id}`);
      setTenantSettings({
        currency_symbol: response.data.currency_symbol || '$',
        tax_name: response.data.tax_name || 'GST',
        tax_rate: response.data.tax_rate ?? 10,
        date_format: response.data.date_format || 'DD/MM/YYYY',
        openai_api_key: response.data.openai_api_key || null,
        xero_connected: response.data.xero_connected || false,
        myob_connected: response.data.myob_connected || false,
        connected_accounting_package: response.data.connected_accounting_package || null
      });
    } catch (error) {
      console.error('Failed to fetch tenant settings');
    }
  };

  const fetchDockets = async () => {
    try {
      const response = await api.get('/dockets');
      setDockets(response.data);
    } catch (error) {
      console.error('Failed to fetch dockets:', error);
      toast.error('Failed to fetch dockets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDockets();
    fetchTenantSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteDocket = async (docketId) => {
    const docket = dockets.find(d => d.id === docketId);
    setDocketToDelete(docket);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!docketToDelete) return;
    setIsDeleting(true);
    try {
      await api.delete(`/dockets/${docketToDelete.id}`);
      toast.success('Docket deleted');
      fetchDockets();
      setDeleteDialogOpen(false);
      setDocketToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete docket');
    } finally {
      setIsDeleting(false);
    }
  };

  // Edit Docket Handlers
  const handleOpenEditDialog = (docket) => {
    // Always open the tabbed drawer (read-only for non-draft statuses)
    setEditingDocket(docket);
    setEditItems(docket.items?.map(item => ({ ...item })) || []);
    setEditNotes(docket.notes || '');
    setUsedPriceList(null); // Reset price list indicator
    setEditDialogOpen(true);
  };

  const handleEditItemChange = (itemId, field, value) => {
    setEditItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, [field]: value } : item
    ));
  };

  const handleAddLineItem = () => {
    const newItem = {
      id: `new-${Date.now()}`,
      item_type: 'equipment',
      type_name: '',
      display_name: '',
      qty: 0,
      uom: 'HOUR',
      unit_price: 0,
      description: '',
      account_item_id: ''
    };
    setEditItems(prev => [...prev, newItem]);
  };

  const handleRemoveLineItem = (itemId) => {
    setEditItems(prev => prev.filter(item => item.id !== itemId));
  };

  const handleSaveEdit = async () => {
    if (!editingDocket) return;
    setSaving(true);
    try {
      await api.put(`/dockets/${editingDocket.id}`, {
        items: editItems,
        notes: editNotes
      });
      toast.success('Docket updated');
      setEditDialogOpen(false);
      fetchDockets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update docket');
    } finally {
      setSaving(false);
    }
  };

  // Recalculate docket items using submitted data and price list
  const handleRecalculatePrices = async () => {
    if (!editingDocket) return;
    setRecalculating(true);
    try {
      const response = await api.post(`/dockets/${editingDocket.id}/recalculate-prices`);
      const recalculatedItems = response.data.items || [];
      const priceListName = response.data.price_list_name;
      
      setEditItems(recalculatedItems);
      setUsedPriceList(priceListName);
      toast.success(`Prices recalculated using "${priceListName}"`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to recalculate prices');
    } finally {
      setRecalculating(false);
    }
  };

  // Submit for Approval
  const handleSubmitForApproval = async (docketId) => {
    setActionLoading(docketId);
    try {
      await api.post(`/dockets/${docketId}/submit-for-approval`);
      toast.success('Docket submitted for approval');
      await fetchDockets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit for approval');
    } finally {
      setActionLoading(null);
    }
  };

  // Approve Docket
  const handleApproveDocket = async (docketId) => {
    setActionLoading(docketId);
    try {
      await api.post(`/dockets/${docketId}/approve`);
      toast.success('Docket approved');
      await fetchDockets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve docket');
    } finally {
      setActionLoading(null);
    }
  };

  // Reject Docket
  const handleRejectDocket = async (docketId) => {
    setActionLoading(docketId);
    try {
      await api.post(`/dockets/${docketId}/reject`);
      toast.success('Docket returned to draft');
      await fetchDockets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject docket');
    } finally {
      setActionLoading(null);
    }
  };

  // Email Docket to Customer
  const handleOpenEmailDialog = async (docket) => {
    setEmailDocket(docket);
    setEmailDialogOpen(true);
    setEmailLoading(true);
    try {
      // Fetch the default customer email
      const response = await api.get(`/dockets/${docket.id}/customer-email`);
      setEmailAddress(response.data.customer_email || '');
    } catch (error) {
      console.error('Failed to fetch customer email:', error);
      setEmailAddress('');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleSendDocketEmail = async () => {
    if (!emailDocket) return;
    
    // Validate email
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailAddress || !emailPattern.test(emailAddress)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setEmailSending(true);
    try {
      await api.post(`/dockets/${emailDocket.id}/email`, {
        to_email: emailAddress
      });
      toast.success(`Docket sent to ${emailAddress}`, {
        description: 'PDF docket has been emailed to the customer'
      });
      setEmailDialogOpen(false);
      setEmailDocket(null);
      setEmailAddress('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send docket email');
    } finally {
      setEmailSending(false);
    }
  };

  // Fetch Email Status
  const handleViewEmailStatus = async (docket) => {
    setEmailDocket(docket);
    setEmailStatusDialogOpen(true);
    setEmailStatusLoading(true);
    try {
      const response = await api.get(`/dockets/${docket.id}/email-status`);
      setEmailStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch email status:', error);
      setEmailStatus(null);
      toast.error('Failed to fetch email status');
    } finally {
      setEmailStatusLoading(false);
    }
  };

  // Resend Docket Email
  const handleResendDocketEmail = async () => {
    if (!emailDocket) return;
    setEmailSending(true);
    try {
      const response = await api.post(`/dockets/${emailDocket.id}/resend-email`);
      toast.success(`Docket resent to ${response.data.sent_to}`, {
        description: 'PDF docket has been resent to the customer'
      });
      // Refresh email status
      const statusResponse = await api.get(`/dockets/${emailDocket.id}/email-status`);
      setEmailStatus(statusResponse.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend docket email');
    } finally {
      setEmailSending(false);
    }
  };

  // Get status badge color
  const getEmailStatusColor = (status) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'opened':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'clicked':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      case 'sent':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'bounced':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'spam':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'delayed':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  // Manual refresh function
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchDockets();
      toast.success('Dockets refreshed');
    } catch (error) {
      toast.error('Failed to refresh dockets');
    } finally {
      setRefreshing(false);
    }
  };

  // Generate PDF
  const handleGeneratePDF = async (docket) => {
    try {
      const response = await api.get(`/dockets/${docket.id}/pdf`, {
        responseType: 'blob'
      });
      // Open in new window for printing
      const blob = new Blob([response.data], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Failed to generate invoice');
    }
  };

  // Export to XERO
  const handleExportToXero = (docket) => {
    if (docket.status !== 'approved') {
      toast.error('Only approved dockets can be exported to Xero');
      return;
    }
    if (docket.xero_invoice_id || docket.status === 'exported') {
      toast.error('This docket has already been exported to Xero');
      return;
    }
    setSelectedDocket(docket);
    setXeroDialogOpen(true);
  };

  // Push docket to Xero
  const handlePushToXero = async () => {
    if (!selectedDocket) return;
    
    // Double-check to prevent duplicates
    if (selectedDocket.xero_invoice_id || selectedDocket.status === 'exported') {
      toast.error('This docket has already been exported to Xero');
      setXeroDialogOpen(false);
      return;
    }
    
    setXeroPushing(true);
    try {
      const response = await api.post('/xero/push-invoice', {
        docket_id: selectedDocket.id
      });
      
      toast.success(`Invoice ${response.data.xero_invoice_number} created in Xero as Draft`);
      setXeroDialogOpen(false);
      fetchDockets(); // Refresh to show updated status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to push to Xero');
    } finally {
      setXeroPushing(false);
    }
  };

  // Export to MYOB
  const handleExportToMyob = (docket) => {
    if (docket.status !== 'approved') {
      toast.error('Only approved dockets can be exported to MYOB');
      return;
    }
    if (docket.myob_invoice_id || docket.status === 'exported') {
      toast.error('This docket has already been exported to MYOB');
      return;
    }
    setSelectedDocket(docket);
    setMyobDialogOpen(true);
  };

  // Push docket to MYOB
  const handlePushToMyob = async () => {
    if (!selectedDocket) return;
    
    // Double-check to prevent duplicates
    if (selectedDocket.myob_invoice_id || selectedDocket.status === 'exported') {
      toast.error('This docket has already been exported to MYOB');
      setMyobDialogOpen(false);
      return;
    }
    
    setMyobPushing(true);
    try {
      const response = await api.post('/myob/push-invoice', {
        docket_id: selectedDocket.id
      });
      
      toast.success(`Invoice ${response.data.invoice_number || response.data.myob_invoice_number} created in MYOB`);
      setMyobDialogOpen(false);
      fetchDockets(); // Refresh to show updated status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to push to MYOB');
    } finally {
      setMyobPushing(false);
    }
  };

  const canApprove = user?.role === 'admin' || user?.role === 'dispatcher';

  const getStatusBadge = (status, docket = null) => {
    switch (status) {
      case 'draft':
        return <Badge variant="secondary" className="bg-gray-100 text-gray-700">Draft</Badge>;
      case 'pending_approval':
        return <Badge variant="secondary" className="bg-amber-100 text-amber-700">Pending Approval</Badge>;
      case 'approved':
        return <Badge variant="secondary" className="bg-green-100 text-green-700">Approved</Badge>;
      case 'exported':
        // Check which accounting package it was exported to
        if (docket?.xero_invoice_id) {
          return <Badge variant="secondary" className="bg-blue-100 text-blue-700">Exported to Xero</Badge>;
        } else if (docket?.myob_invoice_id) {
          return <Badge variant="secondary" className="bg-purple-100 text-purple-700">Exported to MYOB</Badge>;
        }
        return <Badge variant="secondary" className="bg-blue-100 text-blue-700">Exported</Badge>;
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  // OCR Handlers
  const handleOpenOcrDialog = () => {
    if (!tenantSettings.openai_api_key) {
      toast.error('OpenAI API key not configured. Please add your API key in Settings > AI Integration.');
      return;
    }
    setOcrImage(null);
    setOcrImagePreview(null);
    setOcrResult(null);
    setOcrDialogOpen(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      setOcrImage(file);
      setOcrResult(null);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setOcrImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleOcrExtract = async () => {
    if (!ocrImage) {
      toast.error('Please select an image first');
      return;
    }

    setOcrLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', ocrImage);

      const response = await api.post('/dockets/ocr', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        setOcrResult(response.data.extracted);
        toast.success('Docket data extracted successfully!');
      } else {
        toast.error(response.data.error || 'Failed to extract docket data');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extract docket data');
    } finally {
      setOcrLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    // Format based on tenant settings
    const format = tenantSettings.date_format || 'DD/MM/YYYY';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthShort = monthNames[date.getMonth()];
    
    switch (format) {
      case 'MM/DD/YYYY': return `${month}/${day}/${year}`;
      case 'YYYY-MM-DD': return `${year}-${month}-${day}`;
      case 'DD-MMM-YYYY': return `${day}-${monthShort}-${year}`;
      default: return `${day}/${month}/${year}`;
    }
  };

  const calculateSubtotal = (items) => {
    if (!items || items.length === 0) return 0;
    return items.reduce((sum, item) => {
      const qty = parseFloat(item.qty) || 0;
      const price = parseFloat(item.unit_price) || 0;
      return sum + (qty * price);
    }, 0);
  };

  const calculateTax = (subtotal) => {
    const taxRate = tenantSettings.tax_name !== 'None' ? (tenantSettings.tax_rate || 0) : 0;
    return subtotal * (taxRate / 100);
  };

  const calculateTotal = (items) => {
    const subtotal = calculateSubtotal(items);
    return subtotal + calculateTax(subtotal);
  };

  // Get date range based on filter
  const getDateFilterRange = () => {
    const today = new Date();
    switch (dateFilter) {
      case 'today':
        return { from: format(today, 'yyyy-MM-dd'), to: format(today, 'yyyy-MM-dd') };
      case 'yesterday':
        const yesterday = subDays(today, 1);
        return { from: format(yesterday, 'yyyy-MM-dd'), to: format(yesterday, 'yyyy-MM-dd') };
      case 'last_week':
        const lastWeekStart = startOfWeek(subDays(today, 7), { weekStartsOn: 1 });
        const lastWeekEnd = endOfWeek(subDays(today, 7), { weekStartsOn: 1 });
        return { from: format(lastWeekStart, 'yyyy-MM-dd'), to: format(lastWeekEnd, 'yyyy-MM-dd') };
      case 'this_week':
        const thisWeekStart = startOfWeek(today, { weekStartsOn: 1 });
        const thisWeekEnd = endOfWeek(today, { weekStartsOn: 1 });
        return { from: format(thisWeekStart, 'yyyy-MM-dd'), to: format(thisWeekEnd, 'yyyy-MM-dd') };
      case 'all':
      default:
        return { from: null, to: null };
    }
  };

  const filteredDockets = dockets.filter(d => {
    const matchesSearch = 
      d.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.site_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.equipment_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.job_description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || d.status === statusFilter;
    
    // Date filter
    let matchesDate = true;
    if (dateFilter !== 'all') {
      const { from, to } = getDateFilterRange();
      if (from && to && d.job_date) {
        matchesDate = d.job_date >= from && d.job_date <= to;
      }
    }
    
    return matchesSearch && matchesStatus && matchesDate;
  });

  if (loading) {
    return <div className="animate-pulse h-96 bg-muted rounded" />;
  }

  return (
    <div className="space-y-6" data-testid="dockets-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">Dockets</h1>
          <p className="text-muted-foreground">View and manage job dockets for invoicing</p>
        </div>
        {isAiAvailable() && (
          <Button 
            onClick={handleOpenOcrDialog} 
            className="btn-volt"
            data-testid="scan-docket-btn"
          >
            <Camera className="h-4 w-4 mr-2" />
            Scan Docket
            <Badge variant="secondary" className="ml-2 bg-purple-100 text-purple-700 text-xs">AI</Badge>
          </Button>
        )}
      </div>

      <Card className="card-industrial">
        <CardHeader className="pb-3">
          <ListViewHeader
            count={filteredDockets.length}
            countLabel="Dockets"
            onRefresh={handleRefresh}
            refreshing={refreshing}
            
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Search dockets..."
            searchTestId="docket-search"
            
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            statusTestId="docket-status-filter"
            statusOptions={[
              { value: 'all', label: 'All Status' },
              { value: 'draft', label: 'Draft' },
              { value: 'pending_approval', label: 'Pending Approval' },
              { value: 'approved', label: 'Approved' },
            ]}
            
            dateFilter={dateFilter}
            onDateFilterChange={setDateFilter}
            dateFilterOptions={[
              { value: 'today', label: 'Today' },
              { value: 'yesterday', label: 'Yesterday' },
              { value: 'last_week', label: 'Last Week' },
              { value: 'this_week', label: 'This Week' },
              { value: 'all', label: 'All' },
            ]}
          />
        </CardHeader>
        <CardContent>
          {filteredDockets.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {dockets.length === 0 
                  ? 'No dockets yet. Create dockets from confirmed jobs in Jobs & Schedule.'
                  : 'No dockets match your search'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="label-overline">Docket #</TableHead>
                    <TableHead className="label-overline">Date</TableHead>
                    <TableHead className="label-overline">Customer</TableHead>
                    <TableHead className="label-overline">Site</TableHead>
                    <TableHead className="label-overline">Asset</TableHead>
                    <TableHead className="label-overline">Items</TableHead>
                    <TableHead className="label-overline text-right">Total</TableHead>
                    <TableHead className="label-overline">Invoice #</TableHead>
                    <TableHead className="label-overline">Status</TableHead>
                    <TableHead className="label-overline text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDockets.map((docket) => (
                    <TableRow 
                      key={docket.id} 
                      data-testid={`docket-row-${docket.id}`}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={(e) => {
                        // Don't trigger if clicking actions menu
                        if (e.target.closest('button')) return;
                        // Always open the tabbed drawer (Docket + Invoice tabs)
                        handleOpenEditDialog(docket);
                      }}
                    >
                      <TableCell className="font-mono text-sm font-bold text-[#ffd108]">
                        {docket.docket_number ? `#${docket.docket_number}` : '-'}
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatDate(docket.job_date)}
                      </TableCell>
                      <TableCell>{docket.customer_name}</TableCell>
                      <TableCell className="text-muted-foreground">{docket.site_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{docket.equipment_type}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{docket.items?.length || 0} items</Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {tenantSettings.currency_symbol}{calculateTotal(docket.items).toFixed(2)}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {docket.invoice_number || (docket.status === 'exported' ? (docket.xero_invoice_number || docket.myob_invoice_number) : '-')}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(docket.status || 'draft', docket)}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleOpenEditDialog(docket)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {docket.status === 'draft' && (
                              <DropdownMenuItem onClick={() => handleSubmitForApproval(docket.id)}>
                                <Send className="h-4 w-4 mr-2" />
                                Submit for Approval
                              </DropdownMenuItem>
                            )}
                            {docket.status === 'pending_approval' && canApprove && (
                              <>
                                <DropdownMenuItem onClick={() => handleApproveDocket(docket.id)} className="text-green-600" data-testid={`approve-docket-${docket.id}`}>
                                  <Check className="h-4 w-4 mr-2" />
                                  Approve
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleRejectDocket(docket.id)} className="text-amber-600">
                                  <X className="h-4 w-4 mr-2" />
                                  Return to Draft
                                </DropdownMenuItem>
                              </>
                            )}
                            {(docket.status === 'approved' || docket.status === 'exported') && (
                              <>
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenEmailDialog(docket); }} data-testid={`email-docket-${docket.id}`}>
                                  <Mail className="h-4 w-4 mr-2 text-[#ffd108]" />
                                  Email to Customer
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleViewEmailStatus(docket); }} data-testid={`email-status-${docket.id}`}>
                                  <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />
                                  View Email Status
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleGeneratePDF(docket)}>
                                  <FileDown className="h-4 w-4 mr-2" />
                                  Generate Invoice PDF
                                </DropdownMenuItem>
                                {/* Xero Export Option */}
                                {docket.xero_invoice_id ? (
                                  <DropdownMenuItem disabled className="text-blue-600">
                                    <CheckCircle2 className="h-4 w-4 mr-2" />
                                    Exported to Xero
                                  </DropdownMenuItem>
                                ) : docket.status !== 'exported' && tenantSettings.xero_connected && (
                                  <DropdownMenuItem onClick={() => handleExportToXero(docket)}>
                                    <ExternalLink className="h-4 w-4 mr-2 text-[#13B5EA]" />
                                    Push to Xero
                                  </DropdownMenuItem>
                                )}
                                {/* MYOB Export Option */}
                                {docket.myob_invoice_id ? (
                                  <DropdownMenuItem disabled className="text-purple-600">
                                    <CheckCircle2 className="h-4 w-4 mr-2" />
                                    Exported to MYOB
                                  </DropdownMenuItem>
                                ) : docket.status !== 'exported' && tenantSettings.myob_connected && (
                                  <DropdownMenuItem onClick={() => handleExportToMyob(docket)}>
                                    <ExternalLink className="h-4 w-4 mr-2 text-purple-600" />
                                    Push to MYOB
                                  </DropdownMenuItem>
                                )}
                                {/* Show link to settings if no accounting connected */}
                                {!tenantSettings.xero_connected && !tenantSettings.myob_connected && docket.status !== 'exported' && (
                                  <DropdownMenuItem onClick={() => window.location.href = '/settings?tab=accounting'}>
                                    <ExternalLink className="h-4 w-4 mr-2" />
                                    Connect Accounting
                                  </DropdownMenuItem>
                                )}
                              </>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleDeleteDocket(docket.id)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Docket
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Docket Sheet (Flyout Panel) */}
      <Sheet open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <SheetContent side="right" className="w-[800px] sm:max-w-[800px] overflow-y-auto">
          <SheetHeader className="pb-4 border-b">
            <div className="flex items-center justify-between">
              <SheetTitle className="font-heading text-xl">Docket Details</SheetTitle>
              {selectedDocket && getStatusBadge(selectedDocket.status || 'draft')}
            </div>
          </SheetHeader>
          
          {selectedDocket && (
            <div className="space-y-6">
              {/* Header Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/50 rounded-lg">
                <div>
                  <p className="label-overline text-xs">Date</p>
                  <p className="font-medium flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {formatDate(selectedDocket.job_date)}
                  </p>
                </div>
                <div>
                  <p className="label-overline text-xs">Customer</p>
                  <p className="font-medium flex items-center gap-1">
                    <Building2 className="h-4 w-4" />
                    {selectedDocket.customer_name}
                  </p>
                </div>
                <div>
                  <p className="label-overline text-xs">Site</p>
                  <p className="font-medium flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {selectedDocket.site_name}
                  </p>
                </div>
                <div>
                  <p className="label-overline text-xs">Asset</p>
                  <p className="font-medium flex items-center gap-1">
                    <Truck className="h-4 w-4" />
                    {selectedDocket.equipment_type}
                  </p>
                </div>
              </div>

              {selectedDocket.job_description && (
                <div className="p-3 border rounded-lg">
                  <p className="label-overline text-xs mb-1">Job Description</p>
                  <p className="text-sm">{selectedDocket.job_description}</p>
                </div>
              )}

              {/* Items Table */}
              <div>
                <h3 className="font-heading font-semibold mb-3">Docket Items</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="label-overline">Account ID</TableHead>
                      <TableHead className="label-overline">Type</TableHead>
                      <TableHead className="label-overline">Description</TableHead>
                      <TableHead className="label-overline text-right">Qty</TableHead>
                      <TableHead className="label-overline">UOM</TableHead>
                      <TableHead className="label-overline text-right">Unit Price</TableHead>
                      <TableHead className="label-overline text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedDocket.items?.map((item, idx) => (
                      <TableRow key={item.id || idx}>
                        <TableCell className="font-mono text-xs">{item.account_item_id || '-'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Badge 
                              variant={item.item_type === 'equipment' ? 'default' : 'secondary'} 
                              className={`text-xs ${item.item_type === 'material' ? 'bg-orange-500/20 text-orange-400' : ''}`}
                            >
                              {item.item_type === 'equipment' ? 'Equip' : item.item_type === 'material' ? 'Material' : 'Attach'}
                            </Badge>
                            {item.type_name}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                          {item.description || '-'}
                        </TableCell>
                        <TableCell className="text-right font-mono">{item.qty}</TableCell>
                        <TableCell>{item.uom}</TableCell>
                        <TableCell className="text-right font-mono">
                          {tenantSettings.currency_symbol}{parseFloat(item.unit_price || 0).toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right font-mono font-medium">
                          {tenantSettings.currency_symbol}{(parseFloat(item.qty || 0) * parseFloat(item.unit_price || 0)).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                {/* Totals with Tax */}
                <div className="flex justify-end mt-4 pt-4 border-t">
                  <div className="w-64 space-y-2">
                    {(() => {
                      const subtotal = calculateSubtotal(selectedDocket.items);
                      const taxAmount = calculateTax(subtotal);
                      const total = subtotal + taxAmount;
                      const taxRate = tenantSettings.tax_name !== 'None' ? (tenantSettings.tax_rate || 0) : 0;
                      return (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Subtotal</span>
                            <span className="font-mono">{tenantSettings.currency_symbol}{subtotal.toFixed(2)}</span>
                          </div>
                          {taxRate > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">{tenantSettings.tax_name} ({taxRate}%)</span>
                              <span className="font-mono">{tenantSettings.currency_symbol}{taxAmount.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between pt-2 border-t">
                            <span className="font-semibold">Total</span>
                            <span className="text-xl font-bold font-mono text-[#ffd108]">
                              {tenantSettings.currency_symbol}{total.toFixed(2)}
                            </span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {selectedDocket.notes && (
                <div className="p-3 border rounded-lg">
                  <p className="label-overline text-xs mb-1">Notes</p>
                  <p className="text-sm">{selectedDocket.notes}</p>
                </div>
              )}

              {/* Approval Info */}
              {selectedDocket.status === 'approved' && selectedDocket.approved_at && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-700">
                    <CheckCircle2 className="h-4 w-4 inline mr-1" />
                    Approved on {formatDate(selectedDocket.approved_at)}
                  </p>
                </div>
              )}
            </div>
          )}

          <SheetFooter className="pt-4 border-t mt-6 flex-wrap gap-2">
            <div className="flex gap-2 flex-1">
              {selectedDocket?.status === 'approved' && (
                <>
                  <Button variant="outline" onClick={() => handleGeneratePDF(selectedDocket)}>
                    <FileDown className="h-4 w-4 mr-2" />
                    PDF Invoice
                  </Button>
                  {tenantSettings.xero_connected && (
                    <Button variant="outline" onClick={() => { setViewDialogOpen(false); handleExportToXero(selectedDocket); }}>
                      <ExternalLink className="h-4 w-4 mr-2 text-[#13B5EA]" />
                      Export to Xero
                    </Button>
                  )}
                  {tenantSettings.myob_connected && (
                    <Button variant="outline" onClick={() => { setViewDialogOpen(false); handleExportToMyob(selectedDocket); }}>
                      <ExternalLink className="h-4 w-4 mr-2 text-purple-600" />
                      Export to MYOB
                    </Button>
                  )}
                </>
              )}
              {selectedDocket?.status === 'draft' && (
                <Button variant="outline" onClick={() => { setViewDialogOpen(false); handleSubmitForApproval(selectedDocket.id); }}>
                  <Send className="h-4 w-4 mr-2" />
                  Submit for Approval
                </Button>
              )}
              {selectedDocket?.status === 'pending_approval' && canApprove && (
                <>
                  <Button variant="outline" className="text-green-600 border-green-200 hover:bg-green-50" onClick={() => { setViewDialogOpen(false); handleApproveDocket(selectedDocket.id); }} data-testid="approve-docket-btn">
                    <Check className="h-4 w-4 mr-2" />
                    Approve
                  </Button>
                  <Button variant="outline" className="text-amber-600 border-amber-200 hover:bg-amber-50" onClick={() => { setViewDialogOpen(false); handleRejectDocket(selectedDocket.id); }}>
                    <X className="h-4 w-4 mr-2" />
                    Return to Draft
                  </Button>
                </>
              )}
            </div>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* OCR Scan Docket Dialog */}
      <Dialog open={ocrDialogOpen} onOpenChange={setOcrDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Brain className="h-5 w-5 text-purple-500" />
              Scan Docket
              <Badge variant="secondary" className="bg-purple-100 text-purple-700">AI-Powered</Badge>
            </DialogTitle>
            <DialogDescription>
              Upload or capture a photo of a paper docket. AI will extract the information automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Image Upload Area */}
            <div 
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                ocrImagePreview ? 'border-green-300 bg-green-50' : 'border-muted-foreground/25 hover:border-[#ffd108]'
              }`}
            >
              {ocrImagePreview ? (
                <div className="space-y-4">
                  <img 
                    src={ocrImagePreview} 
                    alt="Docket preview" 
                    className="max-h-64 mx-auto rounded-lg shadow-md"
                  />
                  <div className="flex items-center justify-center gap-2 text-green-600">
                    <CheckCircle2 className="h-5 w-5" />
                    <span className="font-medium">{ocrImage?.name}</span>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setOcrImage(null);
                      setOcrImagePreview(null);
                      setOcrResult(null);
                    }}
                  >
                    Choose Different Image
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-center">
                    <div className="p-4 bg-muted rounded-full">
                      <ImageIcon className="h-10 w-10 text-muted-foreground" />
                    </div>
                  </div>
                  <div>
                    <p className="font-medium">Drop your docket image here</p>
                    <p className="text-sm text-muted-foreground">or click to browse</p>
                  </div>
                  <div className="flex gap-2 justify-center">
                    <Button 
                      variant="outline" 
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Image
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => {
                        // For mobile devices, this will open camera
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.capture = 'environment';
                        input.onchange = (e) => handleFileSelect(e);
                        input.click();
                      }}
                    >
                      <Camera className="h-4 w-4 mr-2" />
                      Take Photo
                    </Button>
                  </div>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileSelect}
              />
            </div>

            {/* Extract Button */}
            {ocrImagePreview && !ocrResult && (
              <Button 
                className="w-full btn-volt" 
                onClick={handleOcrExtract}
                disabled={ocrLoading}
              >
                {ocrLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Extracting data...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Extract Docket Data
                  </>
                )}
              </Button>
            )}

            {/* OCR Results */}
            {ocrResult && (
              <Card className="border-green-200 bg-green-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2 text-green-800">
                    <CheckCircle2 className="h-5 w-5" />
                    Extracted Information
                    {ocrResult.confidence && (
                      <Badge variant="outline" className={`ml-auto ${
                        ocrResult.confidence === 'high' ? 'border-green-500 text-green-700' :
                        ocrResult.confidence === 'medium' ? 'border-amber-500 text-amber-700' :
                        'border-red-500 text-red-700'
                      }`}>
                        {ocrResult.confidence} confidence
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="label-overline text-xs text-green-700">Date</p>
                      <p className="font-medium">{ocrResult.date || '-'}</p>
                    </div>
                    <div>
                      <p className="label-overline text-xs text-green-700">Hours</p>
                      <p className="font-medium">{ocrResult.hours || '-'}</p>
                    </div>
                    <div>
                      <p className="label-overline text-xs text-green-700">Customer</p>
                      <p className="font-medium">
                        {ocrResult.matched_customer_name || ocrResult.customer || '-'}
                        {ocrResult.matched_customer_id && (
                          <Badge variant="outline" className="ml-2 text-xs">Matched</Badge>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="label-overline text-xs text-green-700">Site</p>
                      <p className="font-medium">
                        {ocrResult.matched_site_name || ocrResult.site || '-'}
                        {ocrResult.matched_site_id && (
                          <Badge variant="outline" className="ml-2 text-xs">Matched</Badge>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="label-overline text-xs text-green-700">Asset Type</p>
                      <p className="font-medium">
                        {ocrResult.matched_equipment_type || ocrResult.equipment_type || '-'}
                        {ocrResult.matched_equipment_type && (
                          <Badge variant="outline" className="ml-2 text-xs">Matched</Badge>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="label-overline text-xs text-green-700">Operator</p>
                      <p className="font-medium">{ocrResult.operator || '-'}</p>
                    </div>
                  </div>
                  {ocrResult.description && (
                    <div>
                      <p className="label-overline text-xs text-green-700">Description/Notes</p>
                      <p className="font-medium">{ocrResult.description}</p>
                    </div>
                  )}
                  
                  <div className="pt-4 border-t border-green-200">
                    <p className="text-sm text-green-700">
                      <strong>Next step:</strong> Use this information to create a docket from a confirmed job in Jobs & Schedule.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOcrDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Docket Sheet (Flyout Panel) */}
      <Sheet open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <SheetContent side="right" className="w-full max-w-[1200px] sm:w-[1200px] overflow-y-auto">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="font-heading text-xl flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#ffd108]" />
              Docket #{editingDocket?.docket_number}
            </SheetTitle>
          </SheetHeader>
          
          {editingDocket && (
            <Tabs defaultValue="docket" className="mt-4">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="docket" className="flex items-center gap-2">
                  <ClipboardCheck className="h-4 w-4" />
                  Docket
                </TabsTrigger>
                <TabsTrigger value="invoice" className="flex items-center gap-2">
                  <FileDown className="h-4 w-4" />
                  Invoice
                </TabsTrigger>
              </TabsList>
              
              {/* Docket Tab - Submitted Docket Data */}
              <TabsContent value="docket" className="space-y-6 pt-4">
                {/* Header Info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/50 rounded-lg">
                  <div>
                    <p className="label-overline text-xs">Date</p>
                    <p className="font-medium flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {formatDate(editingDocket.job_date)}
                    </p>
                  </div>
                  <div>
                    <p className="label-overline text-xs">Customer</p>
                    <p className="font-medium flex items-center gap-1">
                      <Building2 className="h-4 w-4" />
                      {editingDocket.customer_name}
                    </p>
                  </div>
                  <div>
                    <p className="label-overline text-xs">Site</p>
                    <p className="font-medium flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      {editingDocket.site_name}
                    </p>
                  </div>
                  <div>
                    <p className="label-overline text-xs">Status</p>
                    {getStatusBadge(editingDocket.status || 'draft')}
                  </div>
                </div>

                {/* Submitted Hours */}
                <div>
                  <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                    <Clock className="h-4 w-4 text-[#ffd108]" />
                    Hours Recorded
                  </h3>
                  <div className="space-y-2">
                    {editingDocket.items?.filter(item => item.item_type !== 'material').map((item, idx) => (
                      <div key={item.id || idx} className="bg-muted/50 p-3 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {item.item_type === 'equipment' ? (
                              <div className="w-8 h-8 rounded-full bg-[#ffd108]/20 flex items-center justify-center">
                                <Truck className="h-4 w-4 text-[#ffd108]" />
                              </div>
                            ) : item.item_type === 'worker' ? (
                              <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                                <User className="h-4 w-4 text-blue-500" />
                              </div>
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-gray-500/20 flex items-center justify-center">
                                <Wrench className="h-4 w-4 text-gray-500" />
                              </div>
                            )}
                            <div>
                              <p className="font-medium">{item.display_name || item.type_name}</p>
                              {item.description && (
                                <p className="text-sm text-muted-foreground">{item.description}</p>
                              )}
                              {/* Start Time and Finish Time with labels */}
                              {(item.start_time || item.finish_time) && (
                                <div className="flex items-center gap-4 mt-1 text-xs">
                                  {item.start_time && (
                                    <span className="flex items-center gap-1">
                                      <span className="text-muted-foreground">Start Time:</span>
                                      <span className="font-mono text-foreground">{item.start_time}</span>
                                    </span>
                                  )}
                                  {item.finish_time && (
                                    <span className="flex items-center gap-1">
                                      <span className="text-muted-foreground">Finish Time:</span>
                                      <span className="font-mono text-foreground">{item.finish_time}</span>
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-mono font-bold text-[#ffd108]">{item.qty}h</p>
                          </div>
                        </div>
                        {/* Lunch & Travel breakdown */}
                        {(item.lunch_minutes > 0 || item.travel_minutes > 0) && (
                          <div className="flex items-center gap-4 mt-2 ml-11 text-xs text-muted-foreground">
                            {item.lunch_minutes > 0 && (
                              <span className="flex items-center gap-1">
                                <span className="text-amber-500">Lunch:</span> -{item.lunch_minutes}min
                              </span>
                            )}
                            {item.travel_minutes > 0 && (
                              <span className="flex items-center gap-1">
                                <span className="text-blue-500">Travel:</span> +{item.travel_minutes}min
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Material Loads Section */}
                {editingDocket.material_loads && editingDocket.material_loads.length > 0 && (
                  <div>
                    <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                      <Mountain className="h-4 w-4 text-orange-500" />
                      Material Loads
                    </h3>
                    <div className="space-y-2">
                      {editingDocket.material_loads.map((load, idx) => (
                        <div key={load.id || idx} className="bg-muted/50 p-3 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center">
                                <Package className="h-4 w-4 text-orange-500" />
                              </div>
                              <div>
                                <p className="font-medium">{load.material_name || 'Unknown Material'}</p>
                                <p className="text-sm text-muted-foreground">{load.yard_name || 'Unknown Location'}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-mono font-semibold text-orange-500">
                                {load.quantity} {load.unit}
                              </p>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                load.load_type === 'pickup' ? 'bg-blue-500/20 text-blue-500' :
                                load.load_type === 'disposal' ? 'bg-amber-500/20 text-amber-500' :
                                'bg-green-500/20 text-green-500'
                              }`}>
                                {load.load_type === 'pickup' ? 'Pickup' : 
                                 load.load_type === 'disposal' ? 'Disposal' : 'Buy Back'}
                              </span>
                            </div>
                          </div>
                          {(load.tip_docket_number || load.notes) && (
                            <div className="mt-2 ml-11 text-xs text-muted-foreground">
                              {load.tip_docket_number && (
                                <span className="mr-3">Docket #: {load.tip_docket_number}</span>
                              )}
                              {load.notes && (
                                <span>{load.notes}</span>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Work Completed */}
                {editingDocket.work_completed && (
                  <div>
                    <h3 className="font-heading font-semibold mb-2">Work Completed</h3>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-sm">{editingDocket.work_completed}</p>
                    </div>
                  </div>
                )}

                {/* Notes */}
                {editingDocket.notes && (
                  <div>
                    <h3 className="font-heading font-semibold mb-2">Docket Notes</h3>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-sm">{editingDocket.notes}</p>
                    </div>
                  </div>
                )}

                {/* Customer Sign-off */}
                {editingDocket.customer_signoff_confirmed && (
                  <div className="border-t pt-4">
                    <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Customer Sign-off
                    </h3>
                    <div className="flex items-center gap-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <div>
                        <p className="font-medium">{editingDocket.customer_signoff_name || 'Customer'}</p>
                        <p className="text-sm text-muted-foreground">Confirmed & Signed</p>
                      </div>
                      {editingDocket.customer_signoff_signature && (
                        <div className="ml-auto bg-white rounded p-2">
                          <img 
                            src={editingDocket.customer_signoff_signature} 
                            alt="Customer Signature" 
                            className="h-12 max-w-[150px] object-contain"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Submitted Timestamp */}
                {editingDocket.created_at && (
                  <div className="text-sm text-muted-foreground text-right pt-2 border-t">
                    Submitted: {new Date(editingDocket.created_at).toLocaleString()}
                  </div>
                )}
              </TabsContent>
              
              {/* Invoice Tab - Existing Edit Functionality */}
              <TabsContent value="invoice" className="space-y-6 pt-4">
                {editingDocket?.status === 'draft' ? (
                  <p className="text-sm text-muted-foreground">
                    Edit line items and pricing for invoicing. Changes cannot be made after approval.
                  </p>
                ) : (
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      <AlertCircle className="h-4 w-4 inline mr-1" />
                      This docket is <strong>{editingDocket?.status?.replace('_', ' ')}</strong>. Invoice details are read-only.
                    </p>
                  </div>
                )}
              {/* Header Info - Read Only */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/50 rounded-lg">
                <div>
                  <p className="label-overline text-xs">Date</p>
                  <p className="font-medium flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {formatDate(editingDocket.job_date)}
                  </p>
                </div>
                <div>
                  <p className="label-overline text-xs">Customer</p>
                  <p className="font-medium flex items-center gap-1">
                    <Building2 className="h-4 w-4" />
                    {editingDocket.customer_name}
                  </p>
                </div>
                <div>
                  <p className="label-overline text-xs">Site</p>
                  <p className="font-medium flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    {editingDocket.site_name}
                  </p>
                </div>
                <div>
                  <p className="label-overline text-xs">Status</p>
                  {getStatusBadge(editingDocket.status || 'draft')}
                </div>
              </div>

              {/* Price List Indicator */}
              {usedPriceList && (
                <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span className="text-sm">
                    Prices calculated using: <span className="font-semibold text-green-700 dark:text-green-300">{usedPriceList}</span>
                  </span>
                </div>
              )}

              {/* Editable Items Table */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading font-semibold">Docket Items</h3>
                  <div className="flex items-center gap-2">
                    {editingDocket?.status === 'draft' && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleRecalculatePrices}
                          disabled={recalculating}
                          className="flex items-center gap-2"
                        >
                          {recalculating ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <RotateCcw className="h-4 w-4" />
                          )}
                          Recalculate
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleAddLineItem}
                          className="flex items-center gap-2"
                          data-testid="add-line-item-btn"
                        >
                          <Plus className="h-4 w-4" />
                          Add Line
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                <div className="overflow-x-auto border rounded-lg">
                  <Table className="text-sm">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="label-overline w-[100px] text-xs px-2">Account ID</TableHead>
                        <TableHead className="label-overline text-xs px-2">Description</TableHead>
                        <TableHead className="label-overline w-[80px] text-xs px-2">Qty</TableHead>
                        <TableHead className="label-overline w-[80px] text-xs px-2">UOM</TableHead>
                        <TableHead className="label-overline w-[100px] text-xs px-2 text-right">Price</TableHead>
                        <TableHead className="label-overline w-[100px] text-xs px-2 text-right">Total</TableHead>
                        {editingDocket?.status === 'draft' && (
                          <TableHead className="label-overline w-[40px] text-xs px-1"></TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {editItems.map((item) => (
                        <TableRow key={item.id} className="h-10">
                          <TableCell className="px-2 py-1">
                            <Input
                              value={item.account_item_id || ''}
                              onChange={(e) => handleEditItemChange(item.id, 'account_item_id', e.target.value)}
                              placeholder="ID"
                              className="font-mono text-xs h-7 px-2"
                              disabled={editingDocket?.status !== 'draft'}
                            />
                          </TableCell>
                          <TableCell className="px-2 py-1">
                            <div className="flex items-center gap-2">
                              {item.item_type && (
                                <Badge 
                                  variant={item.item_type === 'equipment' ? 'default' : 'secondary'} 
                                  className={`text-[10px] px-1.5 py-0 shrink-0 ${item.item_type === 'worker' ? 'bg-blue-600 text-white' : ''} ${item.item_type === 'material' ? 'bg-orange-500/20 text-orange-400' : ''}`}
                                >
                                  {item.item_type === 'equipment' ? 'E' : item.item_type === 'worker' ? 'L' : item.item_type === 'material' ? 'M' : 'A'}
                                </Badge>
                              )}
                              <Input
                                value={item.description || item.display_name || item.type_name || ''}
                                onChange={(e) => handleEditItemChange(item.id, 'description', e.target.value)}
                                placeholder="Line item description..."
                                className="text-xs h-7 px-2 flex-1"
                                disabled={editingDocket?.status !== 'draft'}
                              />
                            </div>
                          </TableCell>
                          <TableCell className="px-2 py-1">
                            <Input
                              type="number"
                              step="0.5"
                              min="0"
                              value={item.qty}
                              onChange={(e) => handleEditItemChange(item.id, 'qty', parseFloat(e.target.value) || 0)}
                              className="font-mono text-xs h-7 px-2 w-full text-center"
                              disabled={editingDocket?.status !== 'draft'}
                            />
                          </TableCell>
                          <TableCell className="px-2 py-1">
                            <Select
                              value={item.uom}
                              onValueChange={(v) => handleEditItemChange(item.id, 'uom', v)}
                              disabled={editingDocket?.status !== 'draft'}
                            >
                              <SelectTrigger className="h-7 text-xs px-2">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="HOUR">HOUR</SelectItem>
                                <SelectItem value="DAY">DAY</SelectItem>
                                <SelectItem value="WEEK">WEEK</SelectItem>
                                <SelectItem value="EACH">EACH</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="px-2 py-1 text-right">
                            <Input
                              type="number"
                              step="1"
                              min="0"
                              value={item.unit_price || 0}
                              onChange={(e) => handleEditItemChange(item.id, 'unit_price', parseFloat(e.target.value) || 0)}
                              className="font-mono text-xs h-7 px-2 text-right w-full"
                              disabled={editingDocket?.status !== 'draft'}
                            />
                          </TableCell>
                          <TableCell className="px-2 py-1 text-right font-mono text-xs font-medium">
                            {tenantSettings.currency_symbol}{((parseFloat(item.qty) || 0) * (parseFloat(item.unit_price) || 0)).toFixed(2)}
                          </TableCell>
                          {editingDocket?.status === 'draft' && (
                            <TableCell className="px-1 py-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveLineItem(item.id)}
                                className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                                data-testid={`remove-line-${item.id}`}
                              >
                                <X className="h-3.5 w-3.5" />
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Totals Section */}
                <div className="flex justify-end mt-4 pt-4 border-t">
                  <div className="w-64 space-y-2">
                    {(() => {
                      const subtotal = editItems.reduce((sum, item) => sum + ((parseFloat(item.qty) || 0) * (parseFloat(item.unit_price) || 0)), 0);
                      const taxRate = tenantSettings.tax_name !== 'None' ? (tenantSettings.tax_rate || 0) : 0;
                      const taxAmount = subtotal * (taxRate / 100);
                      const total = subtotal + taxAmount;
                      return (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Subtotal</span>
                            <span className="font-mono">{tenantSettings.currency_symbol}{subtotal.toFixed(2)}</span>
                          </div>
                          {taxRate > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">{tenantSettings.tax_name} ({taxRate}%)</span>
                              <span className="font-mono">{tenantSettings.currency_symbol}{taxAmount.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between pt-2 border-t">
                            <span className="font-semibold">Total</span>
                            <span className="text-xl font-bold font-mono text-[#ffd108]">
                              {tenantSettings.currency_symbol}{total.toFixed(2)}
                            </span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label className="label-overline">Notes</Label>
                <Textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="Additional notes for this docket..."
                  rows={2}
                  disabled={editingDocket?.status !== 'draft'}
                />
              </div>

              <SheetFooter className="pt-4 border-t mt-6">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                  <X className="h-4 w-4 mr-2" />
                  Close
                </Button>
                {editingDocket?.status === 'draft' && (
                  <Button onClick={handleSaveEdit} className="btn-volt" disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                )}
              </SheetFooter>
              </TabsContent>
            </Tabs>
          )}
        </SheetContent>
      </Sheet>

      {/* XERO Export Dialog */}
      <Dialog open={xeroDialogOpen} onOpenChange={setXeroDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="#13B5EA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Push to Xero
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {tenantSettings.xero_connected ? (
              <>
                {/* Connected - Show push confirmation */}
                <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-600" />
                  <p className="font-medium text-green-800 dark:text-green-200">Xero Connected</p>
                  <p className="text-sm text-green-600 dark:text-green-400">Ready to push invoice</p>
                </div>

                {selectedDocket && (
                  <div className="border rounded-lg p-4 space-y-3">
                    <p className="label-overline text-xs">Invoice Details</p>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground text-sm">Customer</span>
                        <span className="font-medium text-sm">{selectedDocket.customer_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground text-sm">Date</span>
                        <span className="font-medium text-sm">{formatDate(selectedDocket.job_date)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground text-sm">Items</span>
                        <span className="font-medium text-sm">{selectedDocket.items?.length || 0} line items</span>
                      </div>
                      <div className="flex justify-between border-t pt-2 mt-2">
                        <span className="font-medium">Total</span>
                        <span className="font-bold text-[#ffd108]">{tenantSettings.currency_symbol}{calculateTotal(selectedDocket.items).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    This will create a <strong>Draft</strong> invoice in Xero. You can review and send it from within Xero.
                  </p>
                </div>
              </>
            ) : (
              <>
                {/* Not Connected */}
                <div className="text-center p-6 bg-muted/50 rounded-lg">
                  <div className="w-16 h-16 mx-auto mb-4 bg-[#13B5EA]/10 rounded-full flex items-center justify-center">
                    <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="#13B5EA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3 className="font-heading font-semibold text-lg mb-2">Connect Xero First</h3>
                  <p className="text-muted-foreground text-sm">
                    Connect your Xero account to push approved dockets as invoices.
                  </p>
                </div>

                {selectedDocket && (
                  <div className="border rounded-lg p-4 space-y-2">
                    <p className="label-overline text-xs">Selected Docket</p>
                    <p className="font-medium">{selectedDocket.customer_name}</p>
                    <p className="text-sm text-muted-foreground">{formatDate(selectedDocket.job_date)} • {tenantSettings.currency_symbol}{calculateTotal(selectedDocket.items).toFixed(2)}</p>
                  </div>
                )}

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800 text-sm">Connection Required</p>
                      <p className="text-amber-700 text-sm mt-1">
                        Go to Settings → Accounting to connect your Xero account.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setXeroDialogOpen(false)}>
              Cancel
            </Button>
            {tenantSettings.xero_connected ? (
              <Button 
                onClick={handlePushToXero}
                disabled={xeroPushing}
                className="bg-[#13B5EA] hover:bg-[#0d9fd1]"
              >
                {xeroPushing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Pushing...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Push to Xero
                  </>
                )}
              </Button>
            ) : (
              <Button 
                onClick={() => window.location.href = '/settings?tab=accounting'}
                className="bg-[#13B5EA] hover:bg-[#0d9fd1]"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Go to Settings
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MYOB Export Dialog */}
      <Dialog open={myobDialogOpen} onOpenChange={setMyobDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="18" height="18" rx="2" stroke="#6B21A8" strokeWidth="2"/>
                <path d="M8 12h8M8 8h8M8 16h5" stroke="#6B21A8" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Push to MYOB
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {tenantSettings.myob_connected ? (
              <>
                {/* Connected - Show push confirmation */}
                <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                  <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-purple-600" />
                  <p className="font-medium text-purple-800 dark:text-purple-200">MYOB Connected</p>
                  <p className="text-sm text-purple-600 dark:text-purple-400">Ready to push invoice</p>
                </div>

                {selectedDocket && (
                  <div className="border rounded-lg p-4 space-y-3">
                    <p className="label-overline text-xs">Invoice Details</p>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground text-sm">Customer</span>
                        <span className="font-medium text-sm">{selectedDocket.customer_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground text-sm">Date</span>
                        <span className="font-medium text-sm">{formatDate(selectedDocket.job_date)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground text-sm">Items</span>
                        <span className="font-medium text-sm">{selectedDocket.items?.length || 0} line items</span>
                      </div>
                      <div className="flex justify-between border-t pt-2 mt-2">
                        <span className="font-medium">Total</span>
                        <span className="font-bold text-[#ffd108]">{tenantSettings.currency_symbol}{calculateTotal(selectedDocket.items).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3">
                  <p className="text-sm text-purple-800 dark:text-purple-200">
                    This will create an <strong>Open</strong> invoice in MYOB. You can review and send it from within MYOB.
                  </p>
                </div>
              </>
            ) : (
              <>
                {/* Not Connected */}
                <div className="text-center p-6 bg-muted/50 rounded-lg">
                  <div className="w-16 h-16 mx-auto mb-4 bg-purple-500/10 rounded-full flex items-center justify-center">
                    <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="3" width="18" height="18" rx="2" stroke="#6B21A8" strokeWidth="2"/>
                      <path d="M8 12h8M8 8h8M8 16h5" stroke="#6B21A8" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <h3 className="font-heading font-semibold text-lg mb-2">Connect MYOB First</h3>
                  <p className="text-muted-foreground text-sm">
                    Connect your MYOB account to push approved dockets as invoices.
                  </p>
                </div>

                {selectedDocket && (
                  <div className="border rounded-lg p-4 space-y-2">
                    <p className="label-overline text-xs">Selected Docket</p>
                    <p className="font-medium">{selectedDocket.customer_name}</p>
                    <p className="text-sm text-muted-foreground">{formatDate(selectedDocket.job_date)} • {tenantSettings.currency_symbol}{calculateTotal(selectedDocket.items).toFixed(2)}</p>
                  </div>
                )}

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800 text-sm">Connection Required</p>
                      <p className="text-amber-700 text-sm mt-1">
                        Go to Settings → Accounting to connect your MYOB account.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setMyobDialogOpen(false)}>
              Cancel
            </Button>
            {tenantSettings.myob_connected ? (
              <Button 
                onClick={handlePushToMyob}
                disabled={myobPushing}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {myobPushing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Pushing...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Push to MYOB
                  </>
                )}
              </Button>
            ) : (
              <Button 
                onClick={() => window.location.href = '/settings?tab=accounting'}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Go to Settings
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Docket to Customer Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Mail className="h-5 w-5 text-[#ffd108]" />
              Email Docket to Customer
            </DialogTitle>
            <DialogDescription>
              Send a PDF copy of this docket to the customer's email
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {emailDocket && (
              <div className="border rounded-lg p-4 space-y-2 bg-muted/30">
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-sm">Docket #</span>
                  <span className="font-medium text-sm">{emailDocket.docket_number || emailDocket.id?.slice(0, 8)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-sm">Customer</span>
                  <span className="font-medium text-sm">{emailDocket.customer_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-sm">Date</span>
                  <span className="font-medium text-sm">{formatDate(emailDocket.job_date)}</span>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email-address">Recipient Email</Label>
              {emailLoading ? (
                <div className="flex items-center justify-center py-2">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  <span className="text-sm text-muted-foreground">Loading customer email...</span>
                </div>
              ) : (
                <Input
                  id="email-address"
                  type="email"
                  placeholder="customer@example.com"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  data-testid="docket-email-input"
                />
              )}
              <p className="text-xs text-muted-foreground">
                Pre-filled with customer's email. You can change it if needed.
              </p>
            </div>

            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                The email will include a <strong>PDF attachment</strong> of the complete docket with all job details, work items, and material loads.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setEmailDialogOpen(false);
              setEmailDocket(null);
              setEmailAddress('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendDocketEmail}
              disabled={emailSending || !emailAddress}
              className="btn-volt"
              data-testid="send-docket-email-btn"
            >
              {emailSending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Status Dialog */}
      <Dialog open={emailStatusDialogOpen} onOpenChange={setEmailStatusDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <History className="h-5 w-5 text-[#ffd108]" />
              Email Delivery Status
            </DialogTitle>
            <DialogDescription>
              Track the delivery status of emails sent for this docket
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {emailStatusLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin mr-2" />
                <span className="text-muted-foreground">Loading email status...</span>
              </div>
            ) : emailStatus ? (
              <>
                {/* Summary Card */}
                <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground text-sm">Docket</span>
                    <span className="font-semibold">#{emailStatus.docket_number || emailDocket?.docket_number}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground text-sm">Emails Sent</span>
                    <span className="font-medium">{emailStatus.email_count || 0}</span>
                  </div>
                  {emailStatus.has_been_emailed && (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground text-sm">Latest Status</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEmailStatusColor(emailStatus.latest_status)}`}>
                          {emailStatus.latest_status?.toUpperCase() || 'UNKNOWN'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground text-sm">Last Sent To</span>
                        <span className="font-medium text-sm">{emailStatus.last_sent_to}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground text-sm">Last Sent At</span>
                        <span className="text-sm">{formatDate(emailStatus.last_sent_at)}</span>
                      </div>
                    </>
                  )}
                </div>

                {/* Email History */}
                {emailStatus.emails && emailStatus.emails.length > 0 ? (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Email History
                    </h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {emailStatus.emails.map((email, index) => (
                        <div key={email.id || index} className="border rounded-lg p-3 space-y-2 bg-card">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">{email.to_email}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getEmailStatusColor(email.status)}`}>
                              {email.status?.toUpperCase() || 'SENT'}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatDate(email.sent_at)}
                            </span>
                            {email.status_updated_at && email.status !== 'sent' && (
                              <span className="flex items-center gap-1">
                                <RefreshCw className="h-3 w-3" />
                                Updated: {formatDate(email.status_updated_at)}
                              </span>
                            )}
                          </div>
                          {email.bounce_reason && (
                            <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded">
                              <strong>Bounce reason:</strong> {email.bounce_reason}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6 text-muted-foreground">
                    <MailX className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No emails have been sent for this docket yet.</p>
                    <p className="text-xs mt-1">Use "Email to Customer" to send the docket.</p>
                  </div>
                )}

                {/* Status Legend */}
                <div className="border-t pt-4 mt-4">
                  <h4 className="text-xs font-semibold text-muted-foreground mb-2">STATUS LEGEND</h4>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full bg-yellow-500`}></span>
                      <span>Sent - Accepted for delivery</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full bg-green-500`}></span>
                      <span>Delivered - Reached inbox</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full bg-blue-500`}></span>
                      <span>Opened - Recipient opened</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full bg-red-500`}></span>
                      <span>Bounced - Delivery failed</span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <AlertCircle className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Unable to load email status.</p>
              </div>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {emailStatus?.has_been_emailed && (
              <Button 
                variant="outline" 
                onClick={handleResendDocketEmail}
                disabled={emailSending}
                className="w-full sm:w-auto"
              >
                {emailSending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Resending...
                  </>
                ) : (
                  <>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Resend to Last Recipient
                  </>
                )}
              </Button>
            )}
            <Button 
              onClick={() => {
                setEmailStatusDialogOpen(false);
                setEmailStatus(null);
                setEmailDocket(null);
              }}
              className={emailStatus?.has_been_emailed ? "w-full sm:w-auto" : ""}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Docket"
        description={`Delete docket "${docketToDelete?.docket_number || ''}"? The job status will be reverted to Confirmed.`}
        confirmText="Delete"
        variant="destructive"
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        testId="delete-docket-dialog"
      />
    </div>
  );
};

export default Dockets;
